import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/moveload/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { ROLE_LABELS, type AppRole } from "@/lib/roles";
import { navDomainsFor, contextualNavFor, primaryNavFor, secondaryNavFor, type NavDomain, type NavEntry } from "@/lib/navigation";
import { LogOut, Menu, MoreHorizontal, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { ImpersonationBanner } from "@/components/founder/ImpersonationBanner";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { UniversalCommand } from "@/components/layout/UniversalCommand";
import { NotificationBell } from "@/components/layout/NotificationBell";

const RAIL_KEY = "moveload.rail.collapsed";

const basePath = (to: string) => to.split("?")[0];

const isDestinationActive = (to: string, end: boolean | undefined, pathname: string) => {
  const base = basePath(to);
  return end ? pathname === base : pathname === base || pathname.startsWith(`${base}/`);
};

const railLinkClass = (active: boolean) =>
  cn(
    "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors min-h-11",
    active
      ? "bg-primary/10 text-primary"
      : "text-muted-foreground hover:bg-muted hover:text-foreground",
  );

/** Adaptive rail: role destinations first, contextual children underneath. */
function RailNav({
  role,
  collapsed,
  onNavigate,
}: {
  role: AppRole | null;
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  const { pathname, search } = useLocation();
  const domains = navDomainsFor(role);
  const contextual = contextualNavFor(role, pathname);
  const current = `${pathname}${search}`;

  const activeDomainId = domains
    .filter((d) => isDestinationActive(d.to, d.end, pathname))
    .sort((a, b) => b.to.length - a.to.length)[0]?.id;

  const domainActive = (d: NavDomain) => {
    if (d.to.includes("?")) return current === d.to;
    if (d.id !== activeDomainId) return false;
    // A query-scoped sibling (e.g. Track) owns the view when its query is present.
    return !domains.some((s) => s.id !== d.id && s.to.includes("?") && current === s.to);
  };

  return (
    <nav className="flex-1 overflow-y-auto p-3" aria-label="Primary">
      <ul className="space-y-1">
        {domains.map((d) => {
          const active = domainActive(d);
          const link = (
            <NavLink
              to={d.to}
              onClick={onNavigate}
              aria-current={active ? "page" : undefined}
              className={cn(railLinkClass(active), collapsed && "justify-center px-0")}
            >
              <d.icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
              {!collapsed && <span className="truncate">{d.label}</span>}
            </NavLink>
          );
          return (
            <li key={d.id}>
              {collapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>{link}</TooltipTrigger>
                  <TooltipContent side="right">{d.label}</TooltipContent>
                </Tooltip>
              ) : (
                link
              )}
            </li>
          );
        })}
      </ul>

      {!collapsed && contextual.length > 0 && (
        <div className="mt-6">
          <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80">
            In this section
          </p>
          <ul className="space-y-1 border-l border-border pl-2">
            {contextual.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors min-h-10",
                      isActive ? "text-primary font-medium bg-primary/5" : "text-muted-foreground hover:text-foreground hover:bg-muted",
                    )
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </nav>
  );
}

/** Everything reachable for the role — used by the mobile "More" drawer. */
function AllDestinations({ role, onNavigate }: { role: AppRole | null; onNavigate?: () => void }) {
  const primary = primaryNavFor(role);
  const secondary = secondaryNavFor(role);
  const render = (items: NavEntry[]) =>
    items.map((item) => (
      <NavLink
        key={`${item.tier}-${item.to}`}
        to={item.to}
        end={item.end}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors min-h-11",
            isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )
        }
      >
        <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="truncate">{item.label}</span>
      </NavLink>
    ));

  return (
    <nav className="flex-1 overflow-y-auto px-4 py-2" aria-label="All destinations">
      {primary.map((domain) => (
        <section key={domain.domain} className="border-b border-border py-3 last:border-0" aria-label={domain.label}>
          {render([domain])}
          <div className="ml-5 border-l border-border pl-2">
            {render(secondary.filter(item => item.domain === domain.domain))}
          </div>
        </section>
      ))}
    </nav>
  );
}

function MobileBottomNav({ role }: { role: AppRole | null }) {
  const { pathname, search } = useLocation();
  const domains = navDomainsFor(role);
  if (role !== "shipper" && role !== "transporter" && role !== "driver" && role !== "founder_owner") return null;
  if (domains.length === 0) return null;

  const visible = domains.slice(0, role === "founder_owner" ? 4 : 5);
  const current = `${pathname}${search}`;
  const activeId = domains.filter(d => d.to.includes("?") ? d.to === current : isDestinationActive(d.to, d.end, pathname))
    .sort((a, b) => Number(b.to.includes("?")) - Number(a.to.includes("?")) || b.to.length - a.to.length)[0]?.id;

  return (
    <nav
      className="md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 px-2 pt-1.5 pb-[max(0.375rem,env(safe-area-inset-bottom))] shadow-elevation-2 backdrop-blur"
      aria-label={`${role ? ROLE_LABELS[role] : "App"} mobile navigation`}
    >
      <div
        className="mx-auto grid max-w-lg gap-1"
        style={{ gridTemplateColumns: `repeat(${visible.length + 1}, minmax(0, 1fr))` }}
      >
        {visible.map((d) => (
          <Button
            key={d.id}
            variant="ghost"
            className="h-14 min-w-0 px-1 py-1 text-muted-foreground data-[active=true]:bg-primary/10 data-[active=true]:text-primary"
            data-active={d.id === activeId}
            asChild
          >
            <NavLink to={d.to} aria-current={d.id === activeId ? "page" : undefined} className="flex flex-col items-center justify-center gap-1">
              <d.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span className="w-full truncate text-center text-[10px] font-semibold leading-none">{d.label}</span>
            </NavLink>
          </Button>
        ))}
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            className="h-14 min-w-0 flex-col gap-1 px-1 py-1 text-muted-foreground"
            aria-label="Open more navigation options"
          >
            <MoreHorizontal className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span className="text-[10px] font-semibold leading-none">More</span>
          </Button>
        </SheetTrigger>
      </div>
    </nav>
  );
}

export default function AppShell() {
  const { role, user, signOut } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(RAIL_KEY) === "1";
  });

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem(RAIL_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  const footer = (compact?: boolean) => (
    <div className="border-t border-border p-3">
      {!compact && (
        <div className="px-3 py-2 text-xs">
          <p className="truncate font-medium text-foreground">{user?.email}</p>
          <p className="text-muted-foreground">{role ? ROLE_LABELS[role] : "No role"}</p>
        </div>
      )}
      <Button
        variant="ghost"
        size="sm"
        aria-label="Sign out"
        className={cn("min-h-11 w-full text-muted-foreground hover:text-foreground", compact ? "justify-center px-0" : "justify-start")}
        onClick={async () => { await signOut(); nav("/"); }}
      >
        <LogOut className={cn("h-4 w-4", !compact && "mr-2")} />
        {!compact && "Sign out"}
      </Button>
    </div>
  );

  const hasMobileBottomNav = role === "shipper" || role === "transporter" || role === "driver" || role === "founder_owner";

  return (
    <TooltipProvider delayDuration={200}>
      <Sheet open={open} onOpenChange={setOpen}>
        <div className="flex min-h-screen w-full flex-col bg-canvas">
          <ImpersonationBanner />

          {/* Mobile top bar */}
          <header className="md:hidden sticky top-0 z-40 flex items-center justify-between gap-2 border-b border-border bg-background/95 px-3 py-2 backdrop-blur">
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open navigation menu" className="min-h-11 min-w-11">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <Logo />
            <div className="flex items-center gap-1">
              <UniversalCommand />
              <NotificationBell />
            </div>
          </header>

          <div className="flex min-h-0 flex-1">
            <aside
              className={cn(
                "hidden shrink-0 flex-col border-r border-border bg-surface transition-[width] duration-200 md:flex",
                collapsed ? "w-[68px]" : "w-64",
              )}
            >
              <div className={cn("flex items-center gap-2 border-b border-border p-4", collapsed ? "justify-center" : "justify-between")}>
                {!collapsed && <Logo />}
                <div className="flex items-center gap-1">
                  {!collapsed && <NotificationBell />}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-muted-foreground"
                    aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
                    aria-pressed={collapsed}
                    onClick={() => setCollapsed((c) => !c)}
                  >
                    {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              {!collapsed && (
                <div className="px-3 pt-3">
                  <UniversalCommand className="w-full" />
                </div>
              )}
              <RailNav role={role} collapsed={collapsed} />
              {footer(collapsed)}
            </aside>

            <main className="min-w-0 flex-1 overflow-y-auto">
              <div className={cn("container px-4 py-5 md:py-8", hasMobileBottomNav && "pb-24 md:pb-8")}>
                <Breadcrumbs />
                <Outlet />
              </div>
            </main>
          </div>

          <MobileBottomNav role={role} />
        </div>

        <SheetContent side="left" className="w-[92vw] max-w-sm bg-surface p-0" aria-describedby={undefined}>
          <div className="flex h-full flex-col">
            <div className="border-b border-border p-5 space-y-4"><Logo /><SheetTitle>More</SheetTitle></div>
            <AllDestinations role={role} onNavigate={() => setOpen(false)} />
            {footer()}
          </div>
        </SheetContent>
      </Sheet>
    </TooltipProvider>
  );
}
